import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth } from "config/firebase";
import {
  Card,
  Typography,
  TextField,
  IconButton,
  Button,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
  Refresh,
} from "@mui/icons-material";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const oobCode = query.get("oobCode") || "";

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [suggestedPassword, setSuggestedPassword] = useState("");

  // Password rules
  const [passwordLengthValid, setPasswordLengthValid] = useState(false);
  const [lowercaseValid, setLowercaseValid] = useState(false);
  const [digitValid, setDigitValid] = useState(false);
  const [specialCharValid, setSpecialCharValid] = useState(false);
  const [affinityValid, setAffinityValid] = useState(false);
  const [strengthScore, setStrengthScore] = useState(0);

  useEffect(() => {
    if (!oobCode) return setError("Invalid or missing reset code.");

    auth
      .verifyPasswordResetCode(oobCode)
      .then((email) => setEmail(email))
      .catch(() => setError("The reset link is invalid or has expired."));
  }, [oobCode]);

  const passwordsMatch = newPassword && newPassword === confirmPassword;

  const checkPasswordStrength = (password) => {
    const lowerPassword = password.toLowerCase();
    const emailParts = email.toLowerCase().split(/[@.]/);

    setPasswordLengthValid(password.length >= 12);
    setLowercaseValid(/[a-z]/.test(password));
    setDigitValid(/\d/.test(password));
    setSpecialCharValid(/[!@#$%^&*(),.?":{}|<>]/.test(password));

    let affinity = true;
    for (const part of emailParts) {
      if (part.length >= 3 && lowerPassword.includes(part)) affinity = false;
    }
    setAffinityValid(affinity);

    let count = 0;
    if (/[a-z]/.test(password)) count++;
    if (/\d/.test(password)) count++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) count++;
    setStrengthScore(count);
  };

  const generateStrongPassword = (length = 14) => {
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const digits = "0123456789";
    const special = "!@#$%^&*()_+~";
    const allChars = lowercase + uppercase + digits + special;

    const getRandomChar = (set) => set[Math.floor(Math.random() * set.length)];
    let password = "";
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const required = [
        getRandomChar(lowercase),
        getRandomChar(uppercase),
        getRandomChar(digits),
        getRandomChar(special),
      ];
      const remaining = Array.from({ length: length - required.length }, () =>
        getRandomChar(allChars)
      );
      password = [...required, ...remaining].sort(() => Math.random() - 0.5).join("");
      if (!email.toLowerCase().split(/[@.]/).some(part => part.length >= 3 && password.toLowerCase().includes(part))) break;
    }


    setSuggestedPassword(password);
    return password;
  };

  const useSuggestedPassword = () => {
    setNewPassword(suggestedPassword);
    setConfirmPassword(suggestedPassword);
    checkPasswordStrength(suggestedPassword);
  };

  const resetPassword = () => {
    setError("");
    setSuccess("");

    if (!passwordsMatch) {
      setError("Passwords do not match.");
      return;
    }

    if (strengthScore < 3 || !passwordLengthValid || !affinityValid) {
      setError("Password does not meet requirements.");
      return;
    }

    auth
      .confirmPasswordReset(oobCode, newPassword)
      .then(() => {
        setSuccess("Password saved successfully! Redirecting...");
        setTimeout(() => navigate("/authentication/sign-in"), 2000);
      })
      .catch((err) => setError("Error resetting password: " + err.message));
  };

  return (
    <>
      {error ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          bgcolor="#1A73E8"
        >  <b style={{ color: 'white' }}>{error}</b></Box>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          bgcolor="#f7faff"
        >

          <Card sx={{ width: 700, height: 750, p: 5, borderRadius: 4, boxShadow: 4 }}>
            {/* Title */}
            <Typography
              variant="h4"
              textAlign="center"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#0070f3" }}
            >
              Create your new password to access your account.
            </Typography>

            {/* Email */}
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
              gutterBottom
            >
              {email}
            </Typography>

            {/* New password */}
            <TextField
              fullWidth
              margin="normal"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your new password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                checkPasswordStrength(e.target.value);
              }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />

            {/* Suggested password */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                bgcolor: "#f1f5ff",
                p: 2,
                borderRadius: 2,
                mt: 1,
              }}
            >
              <Typography
                fontFamily="monospace"
                fontSize={14}
                color="text.primary"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {suggestedPassword || "Suggestion: Generate new password"}
              </Typography>
              {!suggestedPassword ? (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => generateStrongPassword()}
                  startIcon={<Refresh />}
                  sx={{ color: "#0070f3", fontWeight: "bold" }}
                >
                  Generate new password
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={useSuggestedPassword}
                >
                  Use
                </Button>
              )}
            </Box>

            {/* Confirm password */}
            <TextField
              fullWidth
              margin="normal"
              type={showConfirm ? "text" : "password"}
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                checkPasswordStrength(newPassword);
              }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowConfirm(!showConfirm)}>
                    {showConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />

            {/* Requirements */}
            <Box sx={{ mt: 2 }}>
              <Typography fontWeight="bold" gutterBottom fontSize={14}>
                Password requirements:
              </Typography>
              <List dense>
                {[
                  [passwordLengthValid, "Minimum of 12 characters"],
                  [lowercaseValid, "At least one lowercase letter (a–z)"],
                  [digitValid, "At least one digit (0–9)"],
                  [specialCharValid, "At least one special character (!@#$%)"],
                  [affinityValid, "Must not contain your email address"],
                  [passwordsMatch, "Passwords must match"],
                ].map(([valid, text], idx) => (
                  <ListItem key={idx} sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {valid ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Cancel color="error" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      primaryTypographyProps={{
                        fontSize: 14,
                        color: valid ? "success.main" : "error.main",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            <br></br>
            {/* Password strength */}
            <Typography variant="body2" gutterBottom>
              Password strength:{" "}
              {strengthScore >= 3 ? "Strong" : strengthScore === 2 ? "Medium" : "Weak"}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(strengthScore / 4) * 100}
              sx={{
                height: 8,
                borderRadius: 5,
                bgcolor: "#eee",
                "& .MuiLinearProgress-bar": {
                  bgcolor: strengthScore >= 3 ? "success.main" : "error.main",
                },
              }}
            />
            <br></br>
            {/* Submit */}
            <Button
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                py: 1.4,
                fontWeight: "bold",
                borderRadius: 2,
                bgcolor: !passwordsMatch || strengthScore < 3 ? "#cce4ff" : "#0070f3",
                color: "#fff",
                "&:hover": {
                  bgcolor:
                    !passwordsMatch || strengthScore < 3 ? "#cce4ff" : "#0059c9",
                },
              }}
              disabled={!passwordsMatch || strengthScore < 3}
              onClick={resetPassword}
            >
              SAVE PASSWORD
            </Button>

            {/* Error & success */}
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
            {success && (
              <Typography color="success.main" variant="body2" sx={{ mt: 2 }}>
                {success}
              </Typography>
            )}
          </Card>

        </Box >
      )}
    </>
  );
};

export default ResetPassword;
